import React from "react";
import { TextField, MenuItem, TextFieldProps } from "@mui/material";
import { useAccounts } from "../../hooks/useAccounts";

interface AccountPickerProps {
    /** `null`/`undefined`/`""` all render as "Not tagged". */
    value: string | null | undefined;
    /**
     * Always fires with `null` for "Not tagged", never `undefined` — every
     * money-entry form's edit path relies on this to explicitly send
     * `account_id: null` and untag a previously-tagged record. Sending
     * `undefined` instead gets silently dropped by JSON.stringify, which
     * looks like "unset" to a partial-update endpoint and leaves the old
     * tag in place. See ExpenseService.update / AssetService.update_asset,
     * both of which special-case null-vs-omitted for exactly this reason.
     */
    onChange: (accountId: string | null) => void;
    label?: string;
    helperText?: string;
    disabled?: boolean;
    size?: TextFieldProps["size"];
    /** Control the list's open state, e.g. to open it straight after another
     * field so a keyboard user can keep going (POS payments). */
    open?: boolean;
    onOpen?: () => void;
    onClose?: () => void;
}

/**
 * Optional Account (cash/bank/loan) picker shared by every money-entry
 * form — expenses, assets, order payments, purchase payments. Centralizing
 * this avoids each form re-deriving its own null/undefined handling for
 * "not tagged", which is exactly the kind of thing that's easy to get
 * subtly wrong in one of five copies and hard to notice (see git history:
 * the Expense and Asset edit forms both did, and neither could clear a
 * tag once set).
 */
export const AccountPicker: React.FC<AccountPickerProps> = ({
    value,
    onChange,
    label = "Account (Optional)",
    helperText = "Which cash/bank account this money moved through",
    disabled,
    size,
    open,
    onOpen,
    onClose,
}) => {
    const { accounts } = useAccounts();

    return (
        <TextField
            select
            label={label}
            value={value || ""}
            onChange={(e) => onChange(e.target.value || null)}
            fullWidth
            disabled={disabled}
            size={size}
            helperText={helperText}
            slotProps={open === undefined ? undefined : { select: { open, onOpen, onClose } }}
        >
            <MenuItem value="">
                <em>Not tagged</em>
            </MenuItem>
            {accounts.map((account) => (
                <MenuItem key={account.account_id} value={account.account_id}>
                    {account.name}
                </MenuItem>
            ))}
        </TextField>
    );
};
